import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RefreshNowButton — Round 84k Phase 4.
 *
 * Manual on-demand price refresh with server-side rate limit + 60-min
 * lockout. Two scopes:
 *   - 'leaderboard' → up to 25 tickers from this week's race
 *   - 'watchlists'  → up to 15 tickers from the user's 3 public lists
 *
 * Rules (enforced by the backend):
 *   3 refreshes per rolling 5 minutes per scope.
 *   4th attempt → lock for 60 minutes, show live countdown.
 *
 * On success, the parent component can re-fetch its display via the
 * `onRefreshed` callback so the new prices show immediately.
 */
function fmtCountdown(seconds) {
  if (!seconds || seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function RefreshNowButton({ scope, label, onRefreshed }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/refresh-now/${scope}/state`, { withCredentials: true });
      setState(data);
    } catch { /* anon — hide */ setState(null); }
  }, [scope]);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Local 1-second tick that decrements the lock + window timers
  // between server fetches so the user sees a smooth countdown.
  useEffect(() => {
    if (!state) return undefined;
    if (!state.locked && state.window_resets_in_sec <= 0) return undefined;
    tickRef.current = setInterval(() => {
      setState((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        if (next.locked && next.lock_remaining_sec > 0) {
          next.lock_remaining_sec -= 1;
          if (next.lock_remaining_sec <= 0) {
            next.locked = false;
            next.remaining_refreshes = next.limit_per_window;
            next.window_resets_in_sec = next.window_sec;
          }
        }
        if (next.window_resets_in_sec > 0) {
          next.window_resets_in_sec -= 1;
          if (next.window_resets_in_sec <= 0 && !next.locked) {
            next.remaining_refreshes = next.limit_per_window;
            next.window_resets_in_sec = next.window_sec;
          }
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [state]);

  const onClick = async () => {
    if (busy || !state || state.locked || state.remaining_refreshes <= 0) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/community/refresh-now/${scope}`, {}, { withCredentials: true });
      toast.success(`Refreshed ${data.refreshed?.length || 0} ticker${data.refreshed?.length === 1 ? '' : 's'}`, {
        description: `${data.remaining_refreshes} refresh${data.remaining_refreshes === 1 ? '' : 'es'} remaining this 5-min window`,
        duration: 4000,
      });
      onRefreshed?.(data);
      await fetchState();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === 'rate_locked') {
        toast.error('Too many refreshes', {
          description: `Locked for ${fmtCountdown(detail.lock_remaining_sec)}`,
          duration: 5000,
        });
      } else {
        toast.error('Refresh failed', { description: err?.message || 'Unknown error' });
      }
      await fetchState();
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const locked = state.locked && state.lock_remaining_sec > 0;
  const exhausted = !locked && state.remaining_refreshes <= 0;
  const disabled = busy || locked || exhausted;

  let buttonText = label || 'Refresh now';
  let TooltipIcon = RefreshCw;
  // Round 84k Phase 4.1 — color tweak.
  // Idle: light-lavender fill so the text reads on light theme AND on
  //   the violet gradient banner. Border darker for definition.
  // Hover: white fill (no background), violet text — feels like a
  //   "highlighted" state.
  let cls = 'border-violet-400 bg-violet-100 text-violet-800 hover:bg-white hover:text-violet-700 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-600 dark:hover:bg-violet-800/60';
  if (locked) {
    buttonText = `Locked · ${fmtCountdown(state.lock_remaining_sec)}`;
    TooltipIcon = Lock;
    cls = 'border-rose-300/60 dark:border-rose-700/60 text-rose-700 dark:text-rose-300 bg-rose-50/40 dark:bg-rose-950/20 cursor-not-allowed';
  } else if (exhausted) {
    buttonText = `Window reset in ${fmtCountdown(state.window_resets_in_sec)}`;
    TooltipIcon = Lock;
    cls = 'border-amber-300/60 dark:border-amber-700/60 text-amber-700 dark:text-amber-300 bg-amber-50/40 dark:bg-amber-950/20 cursor-not-allowed';
  } else if (busy) {
    buttonText = 'Refreshing…';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={locked ? 'Rate limit hit — try again later' : exhausted ? 'Window full — wait for reset' : `${state.remaining_refreshes} of ${state.limit_per_window} refreshes left this 5-min window`}
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border transition-colors disabled:opacity-80 ${cls}`}
      data-testid={`refresh-now-btn-${scope}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <TooltipIcon className="w-3 h-3" />}
      {buttonText}
      {!locked && !exhausted && !busy && (
        <span className="text-[9px] font-mono opacity-70" data-testid={`refresh-now-remaining-${scope}`}>
          {state.remaining_refreshes}/{state.limit_per_window}
        </span>
      )}
    </button>
  );
}
